import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Colours from '@shared/ui/atoms/Colours';
import { BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { daysBetween } from '@shared/utils/DateUtils';
import { selectTeamProjects } from '@features/wall-planner/pages/wallPlanner.duck';
import { TeamEvent } from '@features/wall-planner/wall-planner.models';

export interface TeamEventLanes {
  events: TeamEvent[];
  laneByEventId: Map<number, number>;
  laneCount: number;
}

// Filters the loaded team events down to the ones intersecting the visible days and packs them
// into lanes (greedy interval partitioning, same as the month view) so events only stack when
// their date ranges actually overlap.
export function useTeamEventLanes(teamId: string, days: Moment[]): TeamEventLanes {
  const allProjects = useSelector(selectTeamProjects);
  return useMemo(() => {
    if (days.length === 0) return { events: [], laneByEventId: new Map(), laneCount: 0 };
    const rangeStart = days[0];
    const rangeEnd = days[days.length - 1];
    const visible = allProjects.filter((event: TeamEvent) => event.teamId === teamId
      && !event.from.isAfter(rangeEnd, 'day') && !event.to.isBefore(rangeStart, 'day'));
    const events = [...visible].sort((a, b) => a.from.valueOf() - b.from.valueOf() || a.to.valueOf() - b.to.valueOf());
    const laneEnds: TeamEvent['to'][] = [];
    const laneByEventId = new Map<number, number>();
    events.forEach(event => {
      let lane = laneEnds.findIndex(end => end.isBefore(event.from, 'day'));
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = event.to;
      laneByEventId.set(event.id, lane);
    });
    return { events, laneByEventId, laneCount: laneEnds.length };
  }, [allProjects, teamId, days]);
}

export function openTeamEvent(event: TeamEvent) {
  window.location.href = `/whereabouts/work-planner/event/${event.id}`;
}

interface Props {
  teamId: string;
  days: Moment[];
  // Show the "Events & projects" heading the monthly view uses and keep the row visible even when
  // no events fall in the visible range. Without it the row collapses entirely when empty.
  showHeading?: boolean;
  // Height in px of each event lane; bars fill the lane.
  rowHeight?: number;
}

// A team's event bars across the visible days, laid out on the same equal-width day columns the
// weekly calendar and daily views use. Bars are clamped to the visible range.
export function TeamEventsRow({ teamId, days, showHeading, rowHeight = 24 }: Props) {
  const { events, laneByEventId } = useTeamEventLanes(teamId, days);
  const { t } = useTranslation();

  if (events.length === 0 && !showHeading) {
    return null;
  }

  return (
    <>
      {showHeading && (
        <EventsHeading>
          <BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('work-planner.events-and-projects')}</BodyVerySmall>
        </EventsHeading>
      )}
      {events.length > 0 && (
        <EventsGrid $dayCount={days.length} $rowHeight={rowHeight}>
          {events.map((event: TeamEvent) => {
            const startIdx = Math.max(0, daysBetween(event.from, days[0]));
            const endIdx = Math.min(days.length - 1, daysBetween(event.to, days[0]));
            return (
              <EventBar key={event.id}
                        style={{
                          gridColumn: `${startIdx + 1} / span ${endIdx - startIdx + 1}`,
                          gridRow: (laneByEventId.get(event.id) ?? 0) + 1,
                        }}
                        title={event.eventName}
                        onClick={() => openTeamEvent(event)}>
                <BodyVerySmall weight={600}>{event.eventName}</BodyVerySmall>
              </EventBar>
            );
          })}
        </EventsGrid>
      )}
    </>
  );
}

const EventsHeading = styled.div`
  padding: 4px 16px 0;
`;

const EventsGrid = styled.div<{ $dayCount: number; $rowHeight: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$dayCount}, 1fr);
  grid-auto-rows: ${props => props.$rowHeight}px;
  gap: 4px;
  padding: 4px 0;
`;

const EventBar = styled.div`
  height: 100%;
  background: #E7FCFF;
  border: 1px solid #1EE1FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  cursor: pointer;
  user-select: none;
  min-width: 0;
  overflow: hidden;
  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
